// Parse + validate the `?return_to=` query param.
//
// The trusted origin list is now sourced from the canonical Janus boundary
// registry (see docs/specs/janus/00-index.md). At build time the script
// `web/scripts/generate-allowed-return-to.mjs` resolves the SSM-backed
// manifest into the static module imported below. The previous hardcoded
// regex (`*.francisquini.ai`) implicitly rejected `cuidari.com` and other
// consumer-brand hosts; the manifest fixes that by listing them
// explicitly.
//
// Allowlist semantics:
//   - Exact (scheme + host[:port]) match against ALLOWED_RETURN_TO_HOSTS.
//   - Backward-compat: any HTTPS `*.francisquini.ai` host or the bare
//     `francisquini.ai` origin is accepted even when not explicitly
//     enumerated in the manifest. This preserves the historical regex
//     behavior so internal subdomains stay reachable without a manifest
//     edit per host.
//   - Local-dev hostnames (`localhost`, `127.0.0.1`) on any port and
//     either http: or https: are accepted unconditionally so dev flows
//     keep working when the manifest is unreachable.

import { ALLOWED_RETURN_TO_HOSTS } from "./_generated/allowed-return-to";

const TRUSTED_LOCAL_HOSTNAMES: readonly string[] = ["localhost", "127.0.0.1"];
const TRUSTED_ROOT_DOMAIN = "francisquini.ai";

// Normalize a URL into its origin form (scheme://host[:port]) for exact
// comparison against the manifest's `surfaces.*.hosts` entries — those
// are already stored as origins.
function originOf(u: URL): string {
  // u.origin includes port iff non-default; matches the manifest format.
  return u.origin;
}

function isTrustedFrancisquiniHost(u: URL): boolean {
  if (u.protocol !== "https:") return false;
  const h = u.hostname;
  return h === TRUSTED_ROOT_DOMAIN || h.endsWith(`.${TRUSTED_ROOT_DOMAIN}`);
}

function isTrustedReturnUrl(u: URL): boolean {
  if (TRUSTED_LOCAL_HOSTNAMES.includes(u.hostname)) return true;
  if (ALLOWED_RETURN_TO_HOSTS.includes(originOf(u))) return true;
  // Backward-compat fallback for the *.francisquini.ai family.
  if (isTrustedFrancisquiniHost(u)) return true;
  return false;
}

export function parseReturnTo(search: string): string | null {
  const params = new URLSearchParams(search);
  const raw = params.get("return_to");
  if (!raw) return null;
  try {
    const u = new URL(raw);
    const isLocal = TRUSTED_LOCAL_HOSTNAMES.includes(u.hostname);
    if (
      isLocal
        ? u.protocol !== "http:" && u.protocol !== "https:"
        : u.protocol !== "https:"
    ) {
      return null;
    }
    if (!isTrustedReturnUrl(u)) return null;
    return u.toString();
  } catch {
    return null;
  }
}

export function appendTokensToHash(
  returnTo: string,
  accessToken: string,
  refreshToken: string,
  userId: string,
): string {
  const frag = new URLSearchParams({
    access_token: accessToken,
    refresh_token: refreshToken,
    user_id: userId,
  }).toString();
  const sep = returnTo.includes("#") ? "&" : "#";
  return `${returnTo}${sep}${frag}`;
}
